<script
  setup
  lang="ts"
  generic="T extends Record<string, string | number | boolean>"
>
type Width = 'default' | 'auto'

type Variant = 'primary' | 'secondary'

type Props = {
  items: T[]
  width?: Width
  variant?: Variant
  emptyPlaceholder?: string
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  width: 'default',
  emptyPlaceholder: '',
})

const model = defineModel<any>()

const componentClasses = computed(() => {
  return [
    'app-select',
    `app-select--variant-${props.variant}`,
    `app-select--width-${props.width}`,
  ]
})

// Нэйминг установлен как props
const contentClass = computed(() => {
  return ['app-select__menu', `app-select__menu--${props.variant}`]
})

const showPlaceholder = computed(() => {
  return props.emptyPlaceholder && !model.value
})
</script>

<template>
  <v-select
    v-model="model"
    :class="componentClasses"
    :menu-props="{ contentClass }"
    dense
    variant="solo"
    v-bind="$attrs"
    :items="items"
  >
    <template v-if="showPlaceholder" #label>
      {{ emptyPlaceholder }}
    </template>

    <template #append-inner>
      <span class="app-select__append-inner">
        <the-icon name="expand-down" color="var(--ui-secondary)" />
      </span>
    </template>
  </v-select>
</template>

<style lang="scss">
/* Общий style, т.к меню находится в overlay */
.app-select__menu {
  padding: 4px 0 !important;
  font-family: 'Raleways', verdana, sans-serif !important;
  border-radius: 10px !important;

  ::-webkit-scrollbar {
    display: none;
  }

  > .v-list {
    padding: 0 !important;
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    box-shadow: 0 6px 30px 0 rgba(50, 52, 72, 0.08) !important;

    .v-list-item--active {
      .v-list-item__overlay {
        background: unset !important;
      }
      .v-list-item__content .v-list-item-title {
        color: var(--ui-primary) !important;
      }
    }

    .v-list-item {
      min-height: 40px;
      padding-inline: 8px !important;

      @include media('>=desktop-mini') {
        padding-inline: 16px !important;
      }

      &:hover {
        background: var(--ui-grey) !important;
      }

      .v-list-item__content .v-list-item-title {
        font-size: 12px;
        font-weight: 600;
        line-height: 16px;
        color: var(--ui-secondary);
      }
    }
  }
}

.app-select__menu--secondary {
  > .v-list {
    background-color: var(--ui-grey) !important;
    box-shadow: none !important;
  }
}
</style>

<style lang="scss" scoped>
.app-select {
  display: block !important;
  flex: none !important;
  font-family: 'Raleways', verdana, sans-serif;
  color: var(--ui-secondary);

  &--variant-primary {
    :deep(.v-field) {
      background-color: var(--ui-white);
    }
  }

  &--variant-secondary {
    :deep(.v-field) {
      background-color: var(--ui-grey);
    }
  }

  &--width-default {
    :deep(.v-input__control) {
      width: 160px;
      height: 40px;
    }
  }

  &--width-auto {
    width: 100%;

    :deep(.v-input__control) {
      width: 100%;
    }
  }

  &__append-inner {
    :deep(svg) {
      fill: var(--ui-secondary);
      stroke: var(--ui-secondary);
    }
  }

  :deep(.v-select__menu-icon) {
    display: none;
  }

  :deep(.v-select__selection) {
    margin-right: 12px !important;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  :deep(.v-select__selection-text) {
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    color: var(--ui-text-primary);
  }

  :deep(.v-field) {
    border-radius: 10px;
    box-shadow: unset !important;
  }

  :deep(.v-field__input) {
    height: 40px;
    min-height: unset;
    padding: 0 8px;
    letter-spacing: 0;

    @include media('>=desktop-mini') {
      padding-left: 16px;
    }
  }

  :deep(.v-label) {
    top: 50% !important;
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    color: var(--ui-text-secondary);
    transform: translateY(-50%) !important;
  }
}
</style>
